import React from 'react';
import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { OrganizationUsageLimits } from 'types/organization_usage_limits';

import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

import { useCurrentPlan } from './useCurrentPlan';
import { useUsageStatistics } from './useUsageStatistics';

export type UnitLimit = {
  planLimit?: number | null;
  userLimit?: number | null;
  usage?: number;
};

export function useUsageLimits(
  options?: UseQueryOptions<OrganizationUsageLimits | null, APIError>
) {
  const { client } = useAPI();
  const { data: organization, isInitialLoading: isLoadingOrganization } =
    useCurrentOrganization();
  const { data: plan, isInitialLoading: isLoadingPlan } = useCurrentPlan();
  const { data: usage, isInitialLoading: isLoadingUsage } =
    useUsageStatistics();

  const { data, isInitialLoading, error } =
    useAuthenticatedQuery<OrganizationUsageLimits | null>(
      ['usage-limits', organization?.id],
      async () => {
        if (!organization) {
          return Promise.reject(new Error('No organization provided.'));
        }

        const { data } = await client.get<OrganizationUsageLimits | null>(
          `/internal/orgs/${organization.id}/usage_limits`
        );

        return data;
      },
      options
    );

  return React.useMemo(
    () => ({
      error,
      cloudStorageBytes: {
        planLimit: plan?.cloud_storage_bytes_limit,
        userLimit: data?.cloud_storage_bytes_limit,
        usage: usage?.cloud_storage_bytes,
      },
      fileProcessingMinutes: {
        planLimit: plan?.file_processing_minutes_limit,
        userLimit: data?.file_processing_minutes_limit,
        usage: usage?.file_processing_minutes,
      },
      monthlyActiveLiveStreams: {
        planLimit: plan?.monthly_active_live_stream_limit,
        userLimit: data?.monthly_active_live_stream_limit,
        usage: usage?.realtime_stream_count,
      },
      runningStreams: {
        planLimit: plan?.running_stream_limit,
        userLimit: data?.running_stream_limit,
        usage: usage?.running_stream_count,
      },
      recordingRetentionDays: {
        planLimit: plan?.recording_retention_days,
        userLimit: data?.recording_retention_days,
        usage: data?.recording_retention_days ?? plan?.recording_retention_days,
      },
      isLoading:
        isInitialLoading ||
        isLoadingOrganization ||
        isLoadingPlan ||
        isLoadingUsage,
    }),
    [
      plan,
      usage,
      isInitialLoading,
      data,
      error,
      isLoadingOrganization,
      isLoadingPlan,
      isLoadingUsage,
    ]
  );
}
